import React, { useCallback } from "react";
import LButton from "@components/LButton";
import LBox from "@components/LBox";
import { ActivityIndicator, Platform } from "react-native";
import LText from "@components/LText";
import { useCheckout } from "./useCheckout";
import { Access } from "@hooks/trpc/useAccesses";
import { useFarcasterContext } from "@hooks/useFarcasterContext";
import useBasePayment from "@hooks/useBasePayment";
import { gbpToUsdc, formatUsdcAmount } from "@utils/currency/gbpToUsdc";
import { usePayment } from "@utils/stripe/usePayment";
import { router } from "expo-router";
import { prefetchAccessDetailHeroImage } from "@utils/accesses/access";
import { storeMoney } from "@libs/platform-wire";

type CheckoutButtonProps = {
  /** An event off the engine (old rail): music unlocks in its own sheet. */
  access: Extract<Access, { type: "event" }>;
  numberOfTickets: number;
  pricePerTicket: number;
  totalAmount: number;
  isValid: boolean;
  email?: string;
  onPaymentSuccess?: () => void;
  unlockCode?: string;
  useGems?: boolean;
  confirmedAmountDueMinorUnits?: number;
};

export default function CheckoutButton({
  access,
  numberOfTickets,
  pricePerTicket,
  totalAmount,
  isValid,
  email,
  onPaymentSuccess,
  unlockCode,
  useGems,
  confirmedAmountDueMinorUnits,
}: CheckoutButtonProps) {
  const { handlePay, isProcessingPayment } = useCheckout({
    access,
    numberOfTickets,
    pricePerTicket,
    unlockCode,
    useGems,
    confirmedAmountDueMinorUnits,
  });
  const { isInMiniApp } = useFarcasterContext();
  const basePayment = useBasePayment();

  const bookingFee = access.bookingFee ?? 0;
  const { checkoutWithBaseTransactionHash } = usePayment(
    access.id,
    numberOfTickets,
    pricePerTicket - bookingFee,
    useGems,
    confirmedAmountDueMinorUnits,
  );

  const isWaitlist = access.isWaitlist === true;

  const handlePressBaseCheckout = useCallback(async () => {
    if (!basePayment.isAvailable) {
      return;
    }

    const usdcAmount = gbpToUsdc(pricePerTicket);

    const result = await basePayment.sendTransaction({
      usdcAmount,
      accessId: access.id,
    });

    if (!result.success || !result.transactionHash) {
      return;
    }

    const checkoutResult = await checkoutWithBaseTransactionHash({
      transactionHash: result.transactionHash,
    });

    if (!checkoutResult) {
      return;
    }

    // This triggers dismiss effects in Checkout.tsx and Tickets.tsx
    onPaymentSuccess?.();

    // On web, replace checkout screen with access detail screen
    // On mobile, set params to trigger dismiss cascade
    if (Platform.OS === "web") {
      prefetchAccessDetailHeroImage(access);
      router.replace({
        pathname: "/(app)/access/[accessId]",
        params: {
          accessId: access.id,
          paymentResult: "success",
        },
      });
    } else {
      router.setParams({
        paymentResult: "success",
        accessId: access.id,
      });
    }
  }, [
    access,
    basePayment,
    checkoutWithBaseTransactionHash,
    pricePerTicket,
    onPaymentSuccess,
  ]);

  const buttonText = isWaitlist
    ? "Join waitlist"
    : totalAmount > 0
      ? `CHECKOUT • ${storeMoney(totalAmount, "GBP")}`
      : "Unlock for free";

  return (
    <LBox gap="s3">
      {isInMiniApp && basePayment.isAvailable && totalAmount > 0 ? (
        <LButton
          label={`${formatUsdcAmount(gbpToUsdc(pricePerTicket))} USDC on Base`}
          onPress={handlePressBaseCheckout}
          sentryEventName="base_usdc_checkout"
          variant="roundedWhiteEmpty"
          paddingVertical="s4"
          enabled={!basePayment.isLoading && isValid}
        />
      ) : null}
      <LButton
        label={buttonText}
        variant="roundedWhite"
        labelNode={
          isProcessingPayment ? (
            <ActivityIndicator size={20} color="black" />
          ) : (
            <LText variant="Body/B1/SemiBold" color="black">
              {buttonText}
            </LText>
          )
        }
        onPress={() => handlePay(email)}
        enabled={!isProcessingPayment && isValid}
        sentryEventName="AccessCheckout.Pay"
        opacity={isValid ? 1 : 0.5}
      />
    </LBox>
  );
}
